import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'antd';

import DataState, { deriveDataState } from '../components/loading/DataState';
import Skeleton from '../components/loading/Skeleton';
import EmptyState from '../components/common/EmptyState';
import ListSearchBox from '../components/items/ListSearchBox';
import CollectionSearchResults from '../components/search/CollectionSearchResults';
import { useCollectionContext } from '../contexts/CollectionContext';
import { useShellSlot } from '../components/shell/ShellSlot';
import useCollectionSearch from '../hooks/useCollectionSearch';
import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_CHARS, intendedTerm } from '../hooks/useListSearch';
import { countItems } from '../services/items';
import { SEARCH_MAX_CHARS } from '../utils/searchRefusal';

/**
 * Search in a collection - `/collections/:collectionId/search?q=<term>`
 * (KPR-355, epic KPE-029; the palette's "Search in this collection" opens it,
 * KPR-356).
 *
 * The grouped view of a term: each CARD once, with its matching items under it
 * and a count, best match first (owner ruling d19). It is its own view: the box
 * on the list narrows that list's rows, this one is where "where in this
 * collection is it" is answered. The server decides what matches, by the same
 * shared rule as the list's box (keepr-api SCHEMA.md "Free text"), so a group's
 * "See all N" opens the list for that card and term with exactly N rows.
 *
 * The term lives in `?q=` and nowhere else: reload and sharing keep it.
 */
export default function CollectionSearchPage() {
	const { collectionId: routeId } = useParams();
	const { collection, resolvedCollectionId } = useCollectionContext() || {};
	const collectionId = resolvedCollectionId || routeId;
	const [params, setParams] = useSearchParams();
	const term = (params.get('q') || '').trim();
	useShellSlot({ crumbTail: 'Search' });

	const usable = Array.from(term).length >= SEARCH_MIN_CHARS ? term : '';
	const { answer, loading, loadingMore, error, loadMore, reload } = useCollectionSearch(collectionId, usable, { enabled: Boolean(collection) });

	// The box: its text is a scratch copy of the term in the address.
	const [text, setText] = useState(term);
	const [notice, setNotice] = useState(null);
	const timer = useRef(null);
	const composing = useRef(false);
	const textRef = useRef(term);
	const termRef = useRef(term);
	termRef.current = term;

	const write = useCallback((value) => {
		clearTimeout(timer.current);
		timer.current = null;
		if (value === termRef.current) return;
		setParams((current) => {
			const next = new URLSearchParams(current);
			if (value) next.set('q', value);
			else next.delete('q');
			return next;
		}, { replace: true });
	}, [setParams]);
	useEffect(() => () => clearTimeout(timer.current), []);

	const settle = useCallback((value) => {
		const trimmed = `${value || ''}`.trim();
		const size = Array.from(trimmed).length;
		if (size > SEARCH_MAX_CHARS) {
			setNotice({ message: `A search is at most ${SEARCH_MAX_CHARS} characters; this one is ${size}.` });
			return;
		}
		setNotice(null);
		write(intendedTerm(trimmed));
	}, [write]);

	const search = useMemo(() => ({
		text,
		notice,
		clear: () => { textRef.current = ''; setText(''); setNotice(null); write(''); },
		onChange: (event) => {
			const value = event?.target?.value ?? '';
			textRef.current = value;
			setText(value);
			if (notice) setNotice(null);
			clearTimeout(timer.current);
			timer.current = null;
			if (!value.trim()) { write(''); return; }
			if (composing.current) return;
			timer.current = setTimeout(() => { timer.current = null; settle(textRef.current); }, SEARCH_DEBOUNCE_MS);
		},
		onCompositionStart: () => { composing.current = true; clearTimeout(timer.current); timer.current = null; },
		onCompositionEnd: (event) => {
			composing.current = false;
			const value = event?.target?.value ?? textRef.current;
			textRef.current = value;
			setText(value);
			clearTimeout(timer.current);
			timer.current = setTimeout(() => { timer.current = null; settle(textRef.current); }, SEARCH_DEBOUNCE_MS);
		},
		onPressEnter: (event) => {
			if (composing.current || event?.nativeEvent?.isComposing) return;
			settle(textRef.current);
		},
	}), [text, notice, write, settle]);

	// The address wins when it moves on without us (Back, a pasted link), but
	// never while a keystroke waits.
	useEffect(() => {
		if (timer.current || composing.current) return;
		if (intendedTerm(textRef.current) === term) return;
		textRef.current = term;
		setText(term);
	}, [term]);

	const answered = Boolean(answer) && answer.term === usable;
	const noHits = answered && !answer.cards.length;

	// A search that finds nothing in a collection with nothing in it is the
	// empty collection, not a miss, and says what to do next.
	const [emptyCollection, setEmptyCollection] = useState(null);
	useEffect(() => {
		setEmptyCollection(null);
		if (!noHits || !collectionId) return undefined;
		let live = true;
		countItems({ collection_id: collectionId, limit: 1 })
			.then((count) => { if (live) setEmptyCollection(count === 0); })
			.catch(() => { if (live) setEmptyCollection(false); });
		return () => { live = false; };
	}, [noHits, collectionId, usable]);

	const state = deriveDataState({
		loading: Boolean(usable) && (loading || (!answered && !error)),
		error: usable ? error : null,
		hasData: answered,
		isEmpty: noHits,
	});

	let summary = null;
	if (answered && answer.cards.length && answer.counted && Number.isFinite(answer.total)) {
		const cards = answer.cards.length;
		summary = `${answer.total.toLocaleString('en-US')} ${answer.total === 1 ? 'item' : 'items'} in ${cards.toLocaleString('en-US')} ${cards === 1 ? 'card' : 'cards'}`;
	}

	const empty = emptyCollection ? (
		<EmptyState
			title='Nothing to search yet'
			description='Add an item to this collection, then search it here.'
			primaryAction={collectionId ? { label: 'Add item', to: `/collections/${collectionId}/items` } : null}
		/>
	) : (
		<div className='collection-search__none' data-testid='no-matches'>
			<span className='collection-search__none-text'>{`No matches for “${usable}”`}</span>
			<Button onClick={search.clear}>Clear search</Button>
		</div>
	);

	return (
		<div className='collection-search'>
			<div className='collection-search__bar'>
				<ListSearchBox
					search={search}
					id='collection-search-box'
					label={collection?.name ? `Search in ${collection.name}` : 'Search in this collection'}
					placeholder={collection?.name ? `Search in ${collection.name}…` : 'Search in this collection…'}
					className='collection-search__box'
				/>
				<Link className='collection-search__back' to={`/collections/${collectionId}/items`}>Back to the list</Link>
			</div>

			{!usable ? (
				<p className='collection-search__prompt'>
					{term ? `Type at least ${SEARCH_MIN_CHARS} characters to search.` : 'Type a word to find the cards and items that hold it.'}
				</p>
			) : (
				<DataState
					state={state}
					hasData={answered}
					isEmpty={noHits}
					skeleton={<Skeleton variant='list-row' count={6} />}
					empty={empty}
					error={error ? error.message : null}
					onRetry={reload}
					label='search results'
				>
					{answered ? (
						<>
							{summary ? <p className='collection-search__summary' data-testid='search-summary'>{summary}</p> : null}
							{answer.skippedProtected ? (
								<p className='collection-search__note' data-testid='protected-note'>
									Protected elements are not searched, so an item may hold the term where search cannot look.
								</p>
							) : null}
							<CollectionSearchResults
								answer={answer}
								term={usable}
								collectionId={collectionId}
								onLoadMore={loadMore}
								loadingMore={loadingMore}
							/>
						</>
					) : null}
				</DataState>
			)}
		</div>
	);
}
